// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Định tuyến tối giản, có nhận biết tiền tố ngôn ngữ.
//
// Không dùng thư viện router: ứng dụng này có bốn đường dẫn, và một thư viện
// router kéo theo mô hình dữ liệu riêng, loader, và một lớp khái niệm mà bốn
// đường dẫn không trả nổi chi phí. Khi số route tăng thì thay — quyết định đó
// nằm gọn trong file này.

import { useEffect, useState } from 'react'

export type Route =
  | { name: 'home' }
  | { name: 'signin' }
  | { name: 'join'; code: string }
  | { name: 'room'; room: string }
  | { name: 'verify'; token: string }
  | { name: 'invite'; token: string }
  | { name: 'recording'; id: string }
  | { name: 'notfound' }

/** fragmentToken đọc token từ FRAGMENT của URL, rồi xoá nó khỏi thanh địa chỉ.
 *
 * Token nằm sau `#` chứ không ở query string là quyết định của ADR-0005 §6:
 * fragment không được gửi lên server, nên nó không vào access log, không vào
 * log của proxy, không vào header Referer.
 *
 * Xoá ngay sau khi đọc vì nó là bí mật dùng một lần — và một URL còn nguyên
 * token là thứ người ta dán vào chat mà không nghĩ. */
function fragmentToken(kind: string): string {
  const hash = location.hash.slice(1)
  if (!hash.startsWith(kind + '=')) return ''
  const token = hash.slice(kind.length + 1)
  history.replaceState(null, '', location.pathname + location.search)
  return token
}

function parse(): Route {
  // Đoạn đầu là ngôn ngữ; bỏ qua ở đây vì i18n đã xử lý.
  const parts = location.pathname.split('/').filter(Boolean).slice(1)
  const [head, second] = parts

  if (!head) {
    const verify = fragmentToken('verify')
    if (verify) return { name: 'verify', token: verify }
    const invite = fragmentToken('invite')
    if (invite) return { name: 'invite', token: invite }
    return { name: 'home' }
  }
  // /{lang}/j/{code} — link mời họp. Ngắn có chủ đích: nó được đọc qua điện
  // thoại và gõ lại bằng tay thường xuyên hơn người ta tưởng.
  if (head === 'j' && second) return { name: 'join', code: second }
  // /{lang}/r/{room} — phòng mở theo TÊN, đường vào của hệ thống ngoài
  // (ADR-0008). Token đi kèm ở `#jwt=…`; xem api.takeAppToken.
  if (head === 'r' && second) return { name: 'room', room: decodeURIComponent(second) }
  // /{lang}/rec/{id} — trang tải bản ghi bằng link chia sẻ.
  //
  // ⚠️ Id nằm trong ĐƯỜNG DẪN, không phải fragment, và đó là điểm khác với
  // /verify và /invite ở trên. Hai cái đó mang TOKEN — thứ tự nó là quyền, nên
  // phải nằm sau `#` để không vào access log. Ở đây id chỉ là địa chỉ; quyền
  // đến từ passcode người dùng gõ vào. Nhờ vậy link chia sẻ được như một link
  // bình thường: dán vào chat, lưu bookmark, mở lại tuần sau.
  if (head === 'rec' && second) return { name: 'recording', id: second }
  if (head === 'signin') return { name: 'signin' }
  if (head === 'verify') return { name: 'verify', token: fragmentToken('token') }
  if (head === 'invite') return { name: 'invite', token: fragmentToken('token') }
  return { name: 'notfound' }
}

export function useRoute(): [Route, (path: string) => void] {
  const [route, setRoute] = useState<Route>(parse)

  useEffect(() => {
    const onPop = () => setRoute(parse())
    addEventListener('popstate', onPop)
    return () => removeEventListener('popstate', onPop)
  }, [])

  // navigate giữ nguyên tiền tố ngôn ngữ: mọi điều hướng trong ứng dụng phải ở
  // lại đúng thứ tiếng người dùng đang đọc.
  const navigate = (path: string) => {
    const lang = location.pathname.split('/')[1] ?? 'en'
    history.pushState(null, '', `/${lang}${path}`)
    setRoute(parse())
  }

  return [route, navigate]
}
